import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bondsAPI, filesAPI, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'sonner';
import {
  ArrowLeft,
  FileText,
  Download,
  File as FileIcon,
  Image as ImageIcon,
  AlertTriangle,
  FolderOpen,
  Eye
} from 'lucide-react';

const BondDocuments = () => {
  const { bondId } = useParams();
  const navigate = useNavigate();

  // Fetch bond data
  const { data: bondData, isPending: isBondLoading } = useQuery({
    queryKey: ['bond', bondId],
    queryFn: () => bondsAPI.getBond(bondId),
    select: data => {
      const responseData = data.data;
      if (responseData && responseData.success && responseData.data) {
        return responseData.data.bond;
      }
      return null;
    },
    enabled: !!bondId
  });

  // Fetch bond documents
  const { data: documents, isPending: isDocumentsLoading } = useQuery({
    queryKey: ['bondDocuments', bondId],
    queryFn: () => bondsAPI.getBondDocuments(bondId),
    select: data => {
      const responseData = data.data;
      if (responseData && responseData.success && responseData.data) {
        return responseData.data.documents || [];
      }
      return [];
    },
    enabled: !!bondId
  });

  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await filesAPI.downloadFile(fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handlePreview = async (fileId, fileName) => {
    try {
      const response = await filesAPI.downloadFile(fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to preview document');
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) {
      return <ImageIcon className="h-5 w-5 text-blue-500" />;
    }
    return <FileIcon className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getCategoryDisplayName = (category) => {
    const categoryMap = {
      bondProspectus: 'Bond Prospectus',
      trustDeed: 'Trust Deed',
      legalDocuments: 'Legal Documents',
      financialReports: 'Financial Reports',
      paymentReceipt: 'Payment Receipt',
      trusteeDocuments: 'Trustee Documents',
      other: 'Other Documents'
    };
    return categoryMap[category] || category;
  };

  // Group documents by category
  const groupedDocuments = documents?.reduce((acc, doc) => {
    const category = doc.documentCategory || doc.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(doc);
    return acc;
  }, {}) || {};

  if (isBondLoading || isDocumentsLoading) {
    return <LoadingSpinner size="lg" text="Loading bond documents..." />;
  }

  if (!bondData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Bond Not Found</h2>
          <p className="text-gray-600 mb-4">The specified bond does not exist.</p>
          <button
            onClick={() => navigate('/primary-subscriber/documents')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <button
              onClick={() => navigate('/primary-subscriber/documents')}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Document Management
            </button>
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Bond Documents
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {bondData.bondName}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bond Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Bond Number</p>
              <p className="text-base font-medium text-gray-900">{bondData.bondNumber || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Issue Date</p>
              <p className="text-base font-medium text-gray-900">
                {bondData.issueDate ? formatDate(bondData.issueDate) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Maturity Date</p>
              <p className="text-base font-medium text-gray-900">
                {bondData.maturityDate ? formatDate(bondData.maturityDate) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="text-base font-medium text-gray-900">{bondData.status}</p>
            </div>
          </div>
        </div>

        {/* Documents */}
        {!documents || documents.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No documents uploaded for this bond</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedDocuments).map((category) => (
              <div key={category} className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {getCategoryDisplayName(category)}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {groupedDocuments[category].length} document{groupedDocuments[category].length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="divide-y divide-gray-200">
                  {groupedDocuments[category].map((doc) => (
                    <div
                      key={doc._id || doc.id || doc.fileId}
                      className="px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {getFileIcon(doc.originalName || doc.fileName || doc.name)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {doc.originalName || doc.fileName || doc.name}
                            </p>
                            <div className="flex items-center space-x-4 mt-1">
                              <p className="text-xs text-gray-500">
                                {formatFileSize(doc.size || doc.fileSize)}
                              </p>
                              <p className="text-xs text-gray-500">
                                Uploaded: {doc.uploadedAt ? formatDate(doc.uploadedAt) : 'N/A'}
                              </p>
                              {doc.uploadedBy && (
                                <p className="text-xs text-gray-500">
                                  By: {doc.uploadedBy.corporateInfo?.companyName || doc.uploadedBy.name || doc.uploadedBy.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handlePreview(doc.fileId || doc._id || doc.id, doc.originalName || doc.fileName || doc.name)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            title="Preview document"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </button>
                          <button
                            onClick={() => handleDownload(doc.fileId || doc._id || doc.id, doc.originalName || doc.fileName || doc.name)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            title="Download document"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BondDocuments;
